import React, { useState, useEffect, useCallback } from 'react';
import { Client } from '../types/client';
import { 
  Typography, 
  Box, 
  Button, 
  Grid, 
  CircularProgress, 
  Chip,
  IconButton
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { CalendarToday, VideoCall, MenuBook, Menu as MenuIcon, Support as SupportIcon, AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ProfileEdit from '../components/Counsellor/ProfileEdit';
import { useNavigate } from 'react-router-dom';
import ThemeBackground from '../components/Layout/ThemeBackground';
import ContractSigningBanner from '../components/Counsellor/ContractSigningBanner';
import CredentialRenewal from '../components/Counsellor/CredentialRenewal';
import MenuDrawer from '../components/Mobile/MenuDrawer';
import BottomNav from '../components/Mobile/BottomNav';
import ClientCard from '../components/Mobile/ClientCard';
import { getClientStatus, getClientStatusChipStyles } from '../utils/clientUtils';
import NotificationBell from '../components/Notifications/NotificationBell';

import SystemNotificationBanner from '../components/Notifications/SystemNotificationBanner';

interface ClientWithNextSession extends Client {
  nextSession?: {
    date: string;
    time: string;
  };
  hasPendingSessionNotes?: boolean;
}

interface DashboardResponse {
  clients: ClientWithNextSession[];
  warning: string | null;
  isActive: boolean;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  type: 'session' | 'assessment';
  counsellor?: string;
  counsellorName?: string;
  client?: string;
  notes?: string;
}

const CounsellorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientWithNextSession[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [, setIsActive] = useState(true);
  const [counsellorId, setCounsellorId] = useState<string | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [appointmentCounts, setAppointmentCounts] = useState({
    scheduled: 0,
    notesDue: 0
  });
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const { user, logout } = useAuth();

  const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        const [clientsResponse, profileResponse, eventsResponse, appointmentsResponse] = await Promise.all([
          api.get<DashboardResponse>('/api/counsellors/clients'),
          api.get('/api/counsellors/profile'),
          api.get<CalendarEvent[]>('/api/events'),
          api.get('/api/appointments/counsellor/me')
        ]);
        
        // Routing logic based on counsellor status:
        // - Active: Show full dashboard
        // - Pending Agreement: Show dashboard with ContractSigningBanner so they can sign the agreement
        // - Pending Self-Completion / has onboardingToken: Existing employee - show dashboard
        // - All other statuses (new applicants in onboarding flow): Redirect to onboarding dashboard
        const status = profileResponse.data.applicationStatus;
        const allowedDashboardStatuses = ['Active', 'Pending Agreement', 'Pending Self-Completion'];
        const isExistingEmployee = !!profileResponse.data.onboardingToken;
        
        if (!allowedDashboardStatuses.includes(status) && !isExistingEmployee) {
          // Redirect NEW applicants in onboarding flow to onboarding dashboard
          navigate('/counsellor/onboarding');
          return;
        }
        
        console.log('[CounsellorDashboard] API Response:', clientsResponse.data);
        console.log('[CounsellorDashboard] Clients received:', clientsResponse.data.clients);
        console.log('[CounsellorDashboard] Sample client fields:', clientsResponse.data.clients?.[0]);
        
        setClients(clientsResponse.data.clients || []);
        setWarning(clientsResponse.data.warning);
        setIsActive(clientsResponse.data.isActive);
        setCounsellorId(profileResponse.data._id);
        setEvents(eventsResponse.data || []);
        
        // Calculate appointment counts
        const appointments = appointmentsResponse.data || [];
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        
        // Count scheduled appointments (future appointments)
        const scheduled = appointments.filter((apt: any) => 
          apt.status === 'scheduled' && new Date(apt.scheduledStart) > now
        ).length;
        
        // Count appointments needing notes (started more than 1 hour ago, still scheduled status, no session note)
        const notesDue = appointments.filter((apt: any) => 
          apt.status === 'scheduled' && 
          new Date(apt.scheduledStart) < oneHourAgo && 
          !apt.linkedSessionNoteId
        ).length;
        
        setAppointmentCounts({ scheduled, notesDue });
        setError(null);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 404) {
          // For new counsellors, redirect to onboarding dashboard
          console.log('Profile not found, redirecting to onboarding dashboard');
          navigate('/counsellor/onboarding');
          return;
        } else if (err.response?.status === 403) {
          setError('You do not have permission to access this profile.');
        } else if (err.response?.status === 401) {
          setError('Your session has expired. Please log in again.');
          logout();
        } else {
          setError('Unable to load your dashboard. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
  }, [navigate, logout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const handleViewProfile = () => {
    if (!counsellorId) {
      console.error('No counsellor ID available');
      return;
    }
    
    // For counsellors, navigate to their profile page using ID
    if (user?.role === 'counsellor' && counsellorId) {
      navigate(`/counsellors/${counsellorId}`);
    } else if (counsellorId) {
      navigate(`/counsellors/${counsellorId}`);
    } else {
      // If no counsellorId is available, show an error message
      setError('Unable to view profile. Please try again later.');
    }
  };

  const handleSupportClick = async () => {
    try {
      // Collect context
      const context = {
        currentUrl: window.location.href,
        pathname: window.location.pathname,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        timestamp: new Date().toISOString()
      };

      // Call backend to create support session
      const response = await api.post<{ supportUrl: string; sessionId: string }>(
        '/api/support/session',
        { context }
      );

      if (!response.data?.supportUrl) {
        throw new Error('No support URL returned from server');
      }

      // Redirect to support portal
      window.location.href = response.data.supportUrl;
    } catch (error) {
      console.error('[SUPPORT] Error:', error);
      alert(`Failed to open support portal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Get upcoming events (next 7 days)
  const getUpcomingEvents = () => {
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return events
      .filter(event => {
        const eventDate = new Date(event.start);
        return eventDate >= now && eventDate <= nextWeek;
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      .slice(0, 5); // Show max 5 upcoming events
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 overflow-y-auto max-h-screen" style={{ paddingBottom: '80px' }}>
        <div className="max-w-7xl mx-auto">
          {/* Contract Signing Banner */}
          <ContractSigningBanner />
          
          {/* System-wide Notification Banner */}
          {user && (
            <SystemNotificationBanner
              appId="counselling"
              clientId="revo"
              environment={process.env.NODE_ENV === 'production' ? 'production' : 'development'}
              userId={user._id}
              userRole={user.role}
            />
          )}
          
          {/* StickyBanner now renders via ProtectedRoute on every page */}

          {/* Header Section */}
          <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex justify-between items-center gap-4">
              <div className="flex-1">
                <Typography 
                  variant="h4" 
                  component="h1" 
                  className="theme-text-primary"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 0.5
                  }}
                >
                  Counsellor Dashboard
                </Typography>
                <Typography 
                  variant="subtitle1"
                  className="theme-text-secondary"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Welcome back, {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Counsellor' : 'Counsellor'}
                </Typography>
              </div>

              {/* Notification Bell - Always visible */}
              <NotificationBell />

              {/* Support Button - Always visible */}
              <IconButton
                onClick={handleSupportClick}
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  width: 48,
                  height: 48,
                  ml: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                  }
                }}
                title="Log Support Issue"
              >
                <SupportIcon />
              </IconButton>

              {/* Mobile Menu Button */}
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  onClick={() => setMenuDrawerOpen(true)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              {/* Desktop Buttons */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleViewProfile}
                  disabled={!counsellorId}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                    }
                  }}
                >
                  View / Edit Profile
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/counsellor/policies')}
                  startIcon={<MenuBook sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                      boxShadow: '0 6px 20px rgba(245, 158, 11, 0.6)'
                    }
                  }}
                >
                  Policies & Procedures
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/mtd')}
                  startIcon={<AccountBalanceIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #c62828 0%, #8e0000 100%)',
                    boxShadow: '0 4px 15px rgba(198, 40, 40, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ad1f1f 0%, #7a0000 100%)',
                      boxShadow: '0 6px 20px rgba(198, 40, 40, 0.6)'
                    }
                  }}
                >
                  Tax Digital
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/settings')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)'
                    }
                  }}
                >
                  Security
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </Box>
            </div>
          </div>

          {/* Warning Message */}
          {warning && (
            <div className="glass-card p-4 mb-6 border-l-4 border-yellow-500">
              <Typography className="theme-text-primary" sx={{ fontWeight: 500 }}>
                ⚠️ {warning}
              </Typography>
            </div>
          )}

          {/* Dashboard Grid */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            {/* Session Actions Card */}
            <Grid item xs={12} md={4}>
              <div className="glass-card h-full min-h-[300px] sm:min-h-[400px]">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                        <CalendarToday sx={{ fontSize: 20, color: 'white' }} />
                      </div>
                      <div className="min-w-0">
                        <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                          Session Actions
                        </Typography>
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Quick overview
                        </Typography>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mt-6">
                    {/* Notes Due */}
                    <div 
                      className="p-4 rounded-lg bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 cursor-pointer hover:border-red-500/40 transition-all active:scale-95"
                      onClick={() => navigate('/counsellor/appointments')}
                      style={{ minHeight: '64px' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          Session Notes Due
                        </Typography>
                        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem' }}>
                            {appointmentCounts.notesDue}
                          </Typography>
                        </div>
                      </div>
                      <Typography className="theme-text-secondary" sx={{ fontSize: '0.75rem' }}>
                        {appointmentCounts.notesDue === 0 
                          ? 'All caught up!' 
                          : `${appointmentCounts.notesDue} session${appointmentCounts.notesDue > 1 ? 's' : ''} need documentation`
                        }
                      </Typography>
                    </div>

                    {/* Upcoming This Week */}
                    <div 
                      className="p-4 rounded-lg bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 cursor-pointer hover:border-blue-500/40 transition-all active:scale-95"
                      onClick={() => navigate('/counsellor/appointments')}
                      style={{ minHeight: '64px' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          Upcoming Appointments
                        </Typography>
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem' }}>
                            {appointmentCounts.scheduled}
                          </Typography>
                        </div>
                      </div>
                      <Typography className="theme-text-secondary" sx={{ fontSize: '0.75rem' }}>
                        {appointmentCounts.scheduled === 0 
                          ? 'No appointments scheduled' 
                          : `${appointmentCounts.scheduled} appointment${appointmentCounts.scheduled > 1 ? 's' : ''} scheduled`
                        }
                      </Typography>
                    </div>

                    {/* View All Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => navigate('/counsellor/appointments')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)'
                        }
                      }}
                    >
                      View All Appointments
                    </Button>
                  </div>
                </div>
              </div>
            </Grid>

            {/* Client Stats Card */}
            <Grid item xs={12} md={8}>
              <div className="glass-card h-full">
                <div className="p-4 sm:p-6">
                  <div className="mb-6">
                    <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 0.5 }}>
                      Client Overview
                    </Typography>
                    <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {clients.length} total clients
                    </Typography>
                  </div>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-600/20 border border-green-500/30">
                        <Typography className="theme-text-primary" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem' }, mb: 1 }}>
                          {(() => {
                            const activeClients = clients.filter(c => {
                              const status = getClientStatus(c);
                              console.log(`[CounsellorDashboard] Client ${c.firstName} ${c.lastName}:`, {
                                status,
                                hasPendingSessionNotes: c.hasPendingSessionNotes,
                                contractReviewedBy: c.contractReviewedBy,
                                currentCounsellorId: c.currentCounsellorId
                              });
                              return status === 'Awaiting Appointment' || status === 'Awaiting Session Notes';
                            });
                            console.log('[CounsellorDashboard] Active clients count:', activeClients.length);
                            return activeClients.length;
                          })()}
                        </Typography>
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, fontWeight: 500 }}>
                          Active Clients
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-600/20 border border-orange-500/30">
                        <Typography className="theme-text-primary" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem' }, mb: 1 }}>
                          {clients.filter(c => getClientStatus(c) === 'Awaiting Session Notes').length}
                        </Typography>
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, fontWeight: 500 }}>
                          Notes Due
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </Grid>
          </Grid>

          {/* Clients Section */}
          <Box id="clients-section" sx={{ mb: { xs: 3, sm: 4 } }}>
            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <div className="glass-card overflow-hidden mb-4">
                <div className="p-4 border-b border-white/10">
                  <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    My Clients ({clients.length})
                  </Typography>
                </div>
              </div>
              {clients.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255, 255, 255, 0.5)' }}>
                  <Typography variant="body1">No clients assigned</Typography>
                </Box>
              ) : (
                <Box sx={{ px: 0 }}>
                  {clients.map((client) => (
                    <ClientCard
                      key={client._id}
                      client={client as any}
                      getClientStatus={getClientStatus}
                      getClientStatusChipStyles={getClientStatusChipStyles}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Desktop Table View */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <div className="glass-card overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-white/10">
              <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                My Clients
              </Typography>
            </div>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                      Client Ref
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                      Referral Type
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                {clients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => (
                    <tr 
                      key={client._id} 
                      onClick={() => navigate(`/client-details/${client._id}`)}
                      className="hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <Typography className="theme-text-primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}>
                          {client.firstName} {client.lastName}
                        </Typography>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {client.ClientRef || 'N/A'}
                        </Typography>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {client.referralCategory === 'organisation' 
                            ? client.organisationDisplayName 
                            : client.referralTypeDisplayName}
                        </Typography>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <Chip
                          label={getClientStatus(client)}
                          sx={{
                            height: { xs: '22px', sm: '26px' },
                            borderRadius: '13px',
                            fontSize: { xs: '0.625rem', sm: '0.75rem' },
                            fontWeight: 600,
                            ...getClientStatusChipStyles(client)
                          }}
                        />
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/clients/${client._id}/sessions`);
                          }}
                          className="text-blue-400 font-semibold hover:text-blue-300 transition-colors text-xs sm:text-sm">
                          Session Notes
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-between items-center px-3 sm:px-6 py-3 sm:py-4 border-t border-white/10">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 0}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-colors ${
                    page === 0
                      ? 'border-white/10 theme-text-secondary cursor-not-allowed'
                      : 'border-white/20 theme-text-primary hover:bg-white/10'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={page >= Math.ceil(clients.length / rowsPerPage) - 1}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-colors ${
                    page >= Math.ceil(clients.length / rowsPerPage) - 1
                      ? 'border-white/10 theme-text-secondary cursor-not-allowed'
                      : 'border-white/20 theme-text-primary hover:bg-white/10'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm theme-text-secondary">
                    Showing{' '}
                    <span className="font-medium">{page * rowsPerPage + 1}</span>
                    {' - '}
                    <span className="font-medium">
                      {Math.min((page + 1) * rowsPerPage, clients.length)}
                    </span>{' '}
                    of <span className="font-medium">{clients.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handleChangePage(null, page - 1)}
                      disabled={page === 0}
                      className={`relative inline-flex items-center px-3 py-2 rounded-l-md border text-sm font-medium transition-colors ${
                        page === 0
                          ? 'border-white/10 theme-text-secondary cursor-not-allowed'
                          : 'border-white/20 theme-text-primary hover:bg-white/10'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handleChangePage(null, page + 1)}
                      disabled={page >= Math.ceil(clients.length / rowsPerPage) - 1}
                      className={`relative inline-flex items-center px-3 py-2 rounded-r-md border text-sm font-medium transition-colors ${
                        page >= Math.ceil(clients.length / rowsPerPage) - 1
                          ? 'border-white/10 theme-text-secondary cursor-not-allowed'
                          : 'border-white/20 theme-text-primary hover:bg-white/10'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
              </div>
            </div>
          </div>
            </Box>
          </Box>

          {/* Credential Renewal Section */}
          <CredentialRenewal />

          {/* Profile Edit Dialog */}
          <ProfileEdit
            open={isEditingProfile}
            onClose={() => setIsEditingProfile(false)}
            onSaved={() => {
              setIsEditingProfile(false);
              // Refresh clients to get any updated data
              const fetchClients = async () => {
                try {
                  const response = await api.get<DashboardResponse>('/api/counsellors/clients');
                  setClients(response.data.clients);
                  setWarning(response.data.warning);
                  setIsActive(response.data.isActive);
                  setError(null);
                } catch (err: any) {
                  console.error('API Error:', err);
                  setError('Unable to refresh your dashboard. Please try again later.');
                }
              };
              fetchClients();
            }}
          />
        </div>

        {/* Mobile Menu Drawer */}
        <MenuDrawer
          open={menuDrawerOpen}
          onClose={() => setMenuDrawerOpen(false)}
          counsellorId={counsellorId || undefined}
        />

        {/* Mobile Bottom Navigation */}
        <BottomNav counsellorId={counsellorId || undefined} />
      </div>
    </ThemeBackground>
  );
};

export default CounsellorDashboard;
